// Custom React Query hooks for Trading Page performance optimization
// This replaces multiple useEffect hooks with optimized caching and state management

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useCallback, useEffect, useRef, useMemo } from 'react'

// Query keys for consistent caching
const TRADING_QUERY_KEYS = {
  chartData: (symbol: string) => ['chartData', symbol] as const,
  layoutAndWidgetMap: ['trading', 'layoutAndWidgetMap'] as const,
  layout: ['trading', 'layout'] as const,
  widgetMap: ['trading', 'widgetMap'] as const,
  userScript: ['trading', 'userScript'] as const,
} as const

// Hook for chart data with caching
export const useChartData = (symbol: string) => {
  return useQuery({
    queryKey: TRADING_QUERY_KEYS.chartData(symbol),
    queryFn: async () => {
      // Check symbol type: .NS/.BS use Yahoo Finance, everything else uses Binance
      const upperSym = symbol.toUpperCase()
      
      if (upperSym.endsWith('.NS') || upperSym.endsWith('.BS')) {
        // Use Yahoo Finance API for Indian stocks (.NS/.BS)
        const response = await fetch(`/api/chart?symbol=${symbol}`)
        if (!response.ok) {
          throw new Error(`Failed to fetch chart data for ${symbol}`)
        }
        return response.json()
      } else {
        // Use Binance API for all other symbols (crypto pairs like SOLETH, SOLUSDT, etc.)
        const response = await fetch(
          `https://api.binance.com/api/v3/klines?symbol=${upperSym}&interval=1d&limit=1000`
        )
        if (!response.ok) {
          throw new Error(`Failed to fetch crypto data for ${symbol}`)
        }
        const data = await response.json()
        
        // Transform Binance data to match expected format
        return data.map((kline: any) => ({
          time: kline[0] / 1000, // Convert to seconds
          open: parseFloat(kline[1]),
          high: parseFloat(kline[2]),
          low: parseFloat(kline[3]),
          close: parseFloat(kline[4]),
          volume: parseFloat(kline[5])
        }))
      }
    },
    enabled: !!symbol,
    staleTime: 30 * 1000, // 30 seconds - chart data updates frequently
    gcTime: 5 * 60 * 1000, // 5 minutes in cache
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

// Hook for persistent layout management
export const useLayoutPersistence = () => {
  const queryClient = useQueryClient()

  // Get layout from cache/localStorage
  const { data: layout } = useQuery({
    queryKey: TRADING_QUERY_KEYS.layout,
    queryFn: () => {
      if (typeof window === 'undefined') return null
      const stored = localStorage.getItem('myLayout')
      return stored ? JSON.parse(stored) : null
    },
    staleTime: Infinity, // Layout doesn't expire
    gcTime: Infinity,
  })

  // Get widget map from cache/localStorage
  const { data: widgetMap } = useQuery({
    queryKey: TRADING_QUERY_KEYS.widgetMap,
    queryFn: () => {
      if (typeof window === 'undefined') return {}
      const stored = localStorage.getItem('myWidgetMap')
      return stored ? JSON.parse(stored) : {}
    },
    staleTime: Infinity,
    gcTime: Infinity,
  })

  // Mutation to update layout
  const updateLayoutMutation = useMutation({
    mutationFn: async (newLayout: any) => {
      if (typeof window !== 'undefined') {
        localStorage.setItem('myLayout', JSON.stringify(newLayout))
      }
      return newLayout
    },
    onSuccess: (newLayout) => {
      queryClient.setQueryData(TRADING_QUERY_KEYS.layout, newLayout)
    }
  })

  // Mutation to update widget map
  const updateWidgetMapMutation = useMutation({
    mutationFn: async (newWidgetMap: Record<string, string>) => {
      if (typeof window !== 'undefined') {
        localStorage.setItem('myWidgetMap', JSON.stringify(newWidgetMap))
      }
      return newWidgetMap
    },
    onSuccess: (newWidgetMap) => {
      queryClient.setQueryData(TRADING_QUERY_KEYS.widgetMap, newWidgetMap)
    }
  })

  return {
    layout,
    widgetMap,
    updateLayout: updateLayoutMutation.mutate,
    updateWidgetMap: updateWidgetMapMutation.mutate,
    isUpdatingLayout: updateLayoutMutation.isPending,
    isUpdatingWidgetMap: updateWidgetMapMutation.isPending,
  }
}

// Hook for user script management
export const useUserScript = () => {
  const queryClient = useQueryClient()

  const { data: userScriptCode } = useQuery({
    queryKey: TRADING_QUERY_KEYS.userScript,
    queryFn: () => {
      if (typeof window === 'undefined') return null
      return localStorage.getItem('userScriptCode')
    },
    staleTime: Infinity,
    gcTime: Infinity,
  })

  const updateScriptMutation = useMutation({
    mutationFn: async (scriptCode: string) => {
      if (typeof window !== 'undefined') {
        localStorage.setItem('userScriptCode', scriptCode)
      }
      return scriptCode
    },
    onSuccess: (scriptCode) => {
      queryClient.setQueryData(TRADING_QUERY_KEYS.userScript, scriptCode)
    }
  })

  const clearScriptMutation = useMutation({
    mutationFn: async () => {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('userScriptCode')
      }
      return null
    },
    onSuccess: () => {
      queryClient.setQueryData(TRADING_QUERY_KEYS.userScript, null)
    }
  })

  return {
    userScriptCode,
    updateUserScript: updateScriptMutation.mutate,
    clearUserScript: clearScriptMutation.mutate,
    isUpdating: updateScriptMutation.isPending,
    isClearing: clearScriptMutation.isPending,
  }
}

// Optimized hook for managing multiple drawer states
export const useDrawerStates = () => {
  const [drawers, setDrawers] = useState({
    chat: false,
    backtester: false,
    optionsBuilder: false,
    events: false,
    sentiment: false,
    hedging: false,
    historicals: false,
    aiHedgeFund: false,
    baskets: false,
    widget: false,
  })

  const [modals, setModals] = useState({
    automations: false,
  })

  // Optimized drawer toggle function
  const toggleDrawer = useCallback((drawerName: keyof typeof drawers) => {
    setDrawers(prev => ({
      ...prev,
      [drawerName]: !prev[drawerName]
    }))
  }, [])

  // Optimized modal toggle function
  const toggleModal = useCallback((modalName: keyof typeof modals) => {
    setModals(prev => ({
      ...prev,
      [modalName]: !prev[modalName]
    }))
  }, [])

  // Close all drawers
  const closeAllDrawers = useCallback(() => {
    setDrawers({
      chat: false,
      backtester: false,
      optionsBuilder: false,
      events: false,
      sentiment: false,
      hedging: false,
      historicals: false,
      aiHedgeFund: false,
      baskets: false,
      widget: false,
    })
  }, [])

  // Close all modals
  const closeAllModals = useCallback(() => {
    setModals({
      automations: false,
    })
  }, [])

  return {
    drawers,
    modals,
    toggleDrawer,
    toggleModal,
    closeAllDrawers,
    closeAllModals,
  }
}

// Optimized hook for grid and window management
export const useGridManagement = (containerRef: React.RefObject<HTMLDivElement | null>) => {
  const [gridWidth, setGridWidth] = useState(1200)

  // Optimized resize handler with debouncing
  const updateGridWidth = useCallback(() => {
    if (containerRef.current) {
      setGridWidth(containerRef.current.offsetWidth)
    } else if (typeof window !== 'undefined') {
      setGridWidth(window.innerWidth)
    }
  }, [containerRef])

  // Debounced update function
  const debouncedUpdate = useCallback(() => {
    let timeoutId: NodeJS.Timeout
    return () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(updateGridWidth, 100)
    }
  }, [updateGridWidth])

  useEffect(() => {
    updateGridWidth()

    if (typeof window !== 'undefined') {
      const debouncedHandler = debouncedUpdate()
      
      window.addEventListener('resize', debouncedHandler)
      window.addEventListener('globalAIStateChange', debouncedHandler)

      return () => {
        window.removeEventListener('resize', debouncedHandler)
        window.removeEventListener('globalAIStateChange', debouncedHandler)
      }
    }
  }, [updateGridWidth, debouncedUpdate])

  // ResizeObserver for container changes
  useEffect(() => {
    if (!containerRef.current) return
    
    const resizeObserver = new ResizeObserver(() => {
      updateGridWidth()
    })
    
    resizeObserver.observe(containerRef.current)
    
    return () => resizeObserver.disconnect()
  }, [updateGridWidth, containerRef])

  return {
    gridWidth,
    updateGridWidth,
  }
}

// Optimized event listener management hook
export const useEventListeners = (drawerCallbacks: Record<string, () => void>) => {
  useEffect(() => {
    // Define event mappings
    const eventMappings = {
      'open-widget-drawer': drawerCallbacks.widget,
      'open-backtester-drawer': drawerCallbacks.backtester,
      'open-optionsbuilder-drawer': drawerCallbacks.optionsBuilder,
      'open-events-drawer': drawerCallbacks.events,
      'open-sentiment-drawer': drawerCallbacks.sentiment,
      'open-hedging-drawer': drawerCallbacks.hedging,
      'open-historicals-drawer': drawerCallbacks.historicals,
      'open-aihedgefund-drawer': drawerCallbacks.aiHedgeFund,
      'open-baskets-drawer': drawerCallbacks.baskets,
      'open-automations-modal': drawerCallbacks.automations,
    }

    // Add all event listeners
    Object.entries(eventMappings).forEach(([event, callback]) => {
      if (callback) {
        window.addEventListener(event, callback)
      }
    })

    // Cleanup function
    return () => {
      Object.entries(eventMappings).forEach(([event, callback]) => {
        if (callback) {
          window.removeEventListener(event, callback)
        }
      })
    }
  }, [drawerCallbacks])
}

// Hook for drag and drop optimization
export const useDragAndDrop = () => {
  const [dragState, setDragState] = useState({
    isDragging: false,
    draggedItemKey: null as string | null,
    dragType: null as string | null,
    activeWidgetKey: null as string | null,
  })

  const updateDragState = useCallback((updates: Partial<typeof dragState>) => {
    setDragState(prev => ({ ...prev, ...updates }))
  }, [])

  const handleDragStart = useCallback((widget: string) => {
    updateDragState({
      dragType: widget,
      isDragging: true,
    })
  }, [updateDragState])

  const handleDragEnd = useCallback(() => {
    updateDragState({
      isDragging: false,
      draggedItemKey: null,
      dragType: null,
    })
  }, [updateDragState])

  const setActiveWidget = useCallback((widgetKey: string | null) => {
    updateDragState({
      activeWidgetKey: widgetKey,
    })
  }, [updateDragState])

  return {
    dragState,
    handleDragStart,
    handleDragEnd,
    setActiveWidget,
    updateDragState,
  }
}

// Hook for optimized symbol management
export const useSymbolManagement = (initialSymbol: string) => {
  const [selectedSymbol, setSelectedSymbol] = useState(initialSymbol)

  const updateSymbol = useCallback((newSymbol: string) => {
    setSelectedSymbol(newSymbol.toUpperCase())
  }, [])

  return {
    selectedSymbol,
    updateSymbol,
  }
}

// Hook for layout and widget map persistence using React Query
export const useLayoutAndWidgetMap = () => {
  const queryClient = useQueryClient()
  
  // Query for combined layout and widget map data
  const { data } = useQuery({
    queryKey: TRADING_QUERY_KEYS.layoutAndWidgetMap,
    queryFn: () => {
      if (typeof window === 'undefined') return { layout: null, widgetMap: null }
      
      const storedLayout = localStorage.getItem('myLayout')
      const storedWidgetMap = localStorage.getItem('myWidgetMap')
      
      return {
        layout: storedLayout ? JSON.parse(storedLayout) : null,
        widgetMap: storedWidgetMap ? JSON.parse(storedWidgetMap) : null
      }
    },
    staleTime: Infinity, // Data doesn't become stale
    gcTime: Infinity, // Keep in cache indefinitely
  })
  
  // Mutation for updating layout and widget map
  const updateMutation = useMutation({
    mutationFn: ({ layout, widgetMap }: { layout?: any, widgetMap?: any }) => {
      if (typeof window !== 'undefined') {
        if (layout !== undefined) {
          localStorage.setItem('myLayout', JSON.stringify(layout))
        }
        if (widgetMap !== undefined) {
          localStorage.setItem('myWidgetMap', JSON.stringify(widgetMap))
        }
      }
      return Promise.resolve({ layout, widgetMap })
    },
    onSuccess: ({ layout, widgetMap }) => {
      // Update the cache with new data
      queryClient.setQueryData(TRADING_QUERY_KEYS.layoutAndWidgetMap, (old: any) => ({
        layout: layout !== undefined ? layout : old?.layout,
        widgetMap: widgetMap !== undefined ? widgetMap : old?.widgetMap
      }))
    }
  })
  
  const updateLayout = useCallback((newLayout: any) => {
    updateMutation.mutate({ layout: newLayout })
  }, [updateMutation])
  
  const updateWidgetMap = useCallback((newWidgetMap: any) => {
    updateMutation.mutate({ widgetMap: newWidgetMap })
  }, [updateMutation])
  
  return {
    layout: data?.layout,
    widgetMap: data?.widgetMap,
    updateLayout,
    updateWidgetMap,
    isLoading: updateMutation.isPending
  }
}
